<template>
  <v-dialog v-model="dialogVisible" max-width="500" persistent>
    <div class="receipt-edit-modal">
      <div class="edit-header">
        <div class="header-title">
          <v-icon size="20" color="white">mdi-pencil</v-icon>
          <span>영수증 수정</span>
        </div>
        <button class="close-btn" @click="closeDialog">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <div class="edit-preview">
        <img :src="imageUrl" :alt="receipt?.fileName || ''" />
      </div>

      <div class="edit-form">
        <div class="form-group">
          <label>
            <v-icon size="14">mdi-calendar</v-icon>
            날짜
          </label>
          <YmdDateInput v-model="form.scanDate" class="form-input" />
        </div>

        <div class="form-group">
          <label>
            <v-icon size="14">mdi-text</v-icon>
            적요
          </label>
          <input
            type="text"
            v-model="form.description"
            class="form-input"
            placeholder="영수증 내용을 입력하세요"
            maxlength="100"
          />
        </div>
      </div>

      <div class="edit-actions">
        <button class="btn-cancel" @click="closeDialog">
          취소
        </button>
        <button
          class="btn-save"
          @click="saveChanges"
          :disabled="saving"
        >
          <v-progress-circular v-if="saving" indeterminate size="16" width="2" color="white" />
          <v-icon v-else size="16">mdi-content-save</v-icon>
          저장
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import YmdDateInput from '@/components/common/YmdDateInput.vue'
import api from '@/services/api'

interface Receipt {
  idx?: number
  fileName: string
  url?: string
  scanDate?: string
  description?: string
  fileUrl?: string
  mCode?: string
  matched?: number
}

const props = defineProps<{
  modelValue: boolean
  receipt: Receipt | null
  mCode: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'saved'): void
}>()

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const saving = ref(false)
const form = ref({
  scanDate: '',
  description: ''
})

const imageUrl = computed(() => {
  if (!props.receipt) return ''
  if (props.receipt.fileUrl) return props.receipt.fileUrl
  if (props.receipt.url) return props.receipt.url
  if (props.mCode && props.receipt.fileName) {
    const isMerged = props.receipt.fileName.startsWith('merged/')
    if (isMerged) {
      const fileName = props.receipt.fileName.replace('merged/', '')
      return `/api/scan/image/${props.mCode}/merged/${fileName}`
    }
    return `/api/scan/image/${props.mCode}/${props.receipt.fileName}`
  }
  return ''
})

const initForm = () => {
  if (props.receipt) {
    form.value.scanDate = props.receipt.scanDate?.split(' ')[0] || ''
    form.value.description = props.receipt.description || ''
  }
}

const saveChanges = async () => {
  if (!props.receipt || !props.mCode) return

  saving.value = true
  try {
    const fileName = props.receipt.fileName
    const response = await api.put(`/scan/meta/${props.mCode}/${encodeURIComponent(fileName)}`, {
      scanDate: form.value.scanDate,
      description: form.value.description
    })

    if (response.data.success) {
      alert('영수증이 수정되었습니다.')
      emit('saved')
      closeDialog()
    } else {
      alert(response.data.message || '수정에 실패했습니다.')
    }
  } catch (error: any) {
    console.error('영수증 수정 오류:', error)
    alert(error.response?.data?.message || '영수증 수정에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

const closeDialog = () => {
  dialogVisible.value = false
}

watch(() => props.modelValue, (open) => {
  if (open) initForm()
})

watch(() => props.receipt, () => {
  if (props.modelValue) initForm()
})
</script>

<style lang="scss" scoped>
.receipt-edit-modal {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
}

.edit-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #1e3a5f;

  &.matched {
    background: #64748b;
  }

  .header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
  }

  .close-btn {
    border: 0;
    background: transparent;
    color: #fff;
    cursor: pointer;
    opacity: 0.85;
    &:hover { opacity: 1; }
  }
}

.matched-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  font-weight: 600;
}

.edit-preview {
  padding: 12px 16px 0;
  img {
    width: 100%;
    max-height: 280px;
    object-fit: contain;
    border-radius: 8px;
    background: #f1f5f9;
  }
}

.edit-form {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &.disabled {
    opacity: 0.65;
    pointer-events: none;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: #475569;
      font-size: 13px;
      font-weight: 600;
    }

    .form-input {
      width: 100%;
      height: 38px;
      padding: 0 10px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      font-size: 14px;
    }
  }
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 16px 16px;

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border-radius: 6px;
    border: 1px solid transparent;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  .btn-cancel {
    background: #fff;
    border-color: #cbd5e1;
    color: #475569;
  }

  .btn-save {
    background: #2563eb;
    color: #fff;
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
